<template>
  <div class="page-use-cases">
    <main>
      <section class="relative overflow-hidden bg-gray-50">
        <div class="absolute inset-0 bg-gradient-to-r from-brand/5 via-transparent to-brand/5"></div>
        <div class="mx-auto max-w-[1400px] px-4 py-16 lg:flex lg:items-center lg:gap-12 lg:px-8 lg:py-20">
          <div class="relative z-10 space-y-4 lg:w-7/12">
            <p class="text-xs font-semibold uppercase text-muted">Invi8 for every invite</p>
            <h1 class="text-3xl font-semibold leading-tight text-ink sm:text-4xl lg:text-5xl">Create, invite, and coordinate for all kinds of events.</h1>
            <p class="text-lg text-muted">From birthdays and weddings to community gatherings and team events, Invi8 keeps the invitation, guest activity, and nearby event services connected.</p>
            <div class="flex flex-wrap gap-3">
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-brand shadow-card ring-1 ring-gray-100">Instant RSVPs</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">Guest management</span>
              <span class="rounded-full bg-white px-4 py-2 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-100">Works on any device</span>
            </div>
          </div>
          <div class="relative z-10 mt-10 flex-1 lg:mt-0">
            <div class="rounded-3xl bg-white p-6 shadow-card ring-1 ring-gray-100">
              <img src="/assets/illustrations/team-collaboration.svg" alt="A team planning together" class="mb-6 h-56 w-full rounded-2xl bg-orange-50/60 object-contain p-5" />
              <div class="flex items-center justify-between">
                <p class="text-sm font-semibold text-ink">Popular playbooks</p>
                <span class="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">Multi-event ready</span>
              </div>
              <ul class="mt-4 space-y-3 text-sm text-muted">
                <li class="flex items-start gap-2">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Swap details for multiple dates and guest lists in minutes.
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Centralize invitations, responses, guest activity, and event updates.
                </li>
                <li class="flex items-start gap-2">
                  <span class="mt-1 h-2 w-2 rounded-full bg-brand"></span>
                  Use photos, posters, GIFs, and videos to match the tone of each event.
                </li>
              </ul>
              <div class="mt-6 flex flex-wrap gap-3">
                <RouterLink class="rounded-full bg-brand px-5 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/register">Create an invitation</RouterLink>
                <RouterLink class="rounded-full bg-white px-5 py-3 text-sm font-semibold text-ink shadow-card ring-1 ring-gray-200 transition hover:-translate-y-0.5" to="/vendors">Find vendors</RouterLink>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section class="mx-auto max-w-[1400px] px-4 py-12 lg:px-8">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p class="text-xs font-semibold uppercase text-muted">Use cases</p>
            <h2 class="text-2xl font-semibold text-ink">Build invites and lineups for any crowd</h2>
          </div>
          <p class="text-sm text-muted sm:max-w-md">Mix and match guest segments, add RSVPs, and attach vendors so everyone knows where to be and when.</p>
        </div>
        <div class="mt-6 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          <article
            v-for="useCase in useCases"
            :key="useCase.title"
            class="flex h-full flex-col justify-between rounded-3xl p-6 shadow-card ring-1 ring-gray-100 transition hover:-translate-y-1 hover:shadow-lg"
            :style="{ background: useCase.bg }"
          >
            <div class="space-y-3">
              <div class="inline-flex items-center gap-2 rounded-full bg-gray-50 px-3 py-1 text-xs font-semibold text-brand">
                <span>{{ useCase.icon }}</span>
                <span>{{ useCase.badge }}</span>
              </div>
              <h3 class="text-lg font-semibold text-ink">{{ useCase.title }}</h3>
              <p class="text-sm text-muted">{{ useCase.copy }}</p>
            </div>
            <ul class="mt-4 space-y-2 text-sm text-muted">
              <li v-for="(item, index) in useCase.points" :key="index" class="flex items-start gap-2">
                <span class="mt-1 h-1.5 w-1.5 rounded-full bg-brand"></span>
                <span>{{ item }}</span>
              </li>
            </ul>
            <RouterLink
              class="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-white/80 px-4 py-2 text-sm font-semibold text-brand shadow-card ring-1 ring-gray-200 transition hover:-translate-y-0.5 hover:bg-white"
              :to="`/use-cases/${useCase.slug}`"
            >
              How it works
              <span aria-hidden="true">→</span>
            </RouterLink>
          </article>
        </div>
      </section>

      <section class="bg-gray-900">
        <div class="mx-auto max-w-[1400px] px-4 py-12 text-white lg:px-8">
          <div class="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div class="space-y-4">
              <p class="text-xs font-semibold uppercase tracking-wide text-gray-400">Built for teams</p>
              <h2 class="text-3xl font-semibold leading-tight">Keep every event on-brand and on-schedule.</h2>
              <p class="text-lg text-gray-300">Co-host support, guest responses, event updates, and reusable event details help teams coordinate from one shared place.</p>
              <div class="grid gap-3 sm:grid-cols-2">
                <div class="rounded-2xl bg-white/5 p-4">
                  <p class="text-sm font-semibold">Unified RSVPs</p>
                  <p class="text-sm text-gray-300">Add guests, monitor responses, and keep the event list organized.</p>
                </div>
                <div class="rounded-2xl bg-white/5 p-4">
                  <p class="text-sm font-semibold">Vendor favorites</p>
                  <p class="text-sm text-gray-300">Discover nearby venue and service profiles relevant to the event.</p>
                </div>
                <div class="rounded-2xl bg-white/5 p-4">
                  <p class="text-sm font-semibold">Brand controls</p>
                  <p class="text-sm text-gray-300">Use event media and details to create a consistent invitation experience.</p>
                </div>
                <div class="rounded-2xl bg-white/5 p-4">
                  <p class="text-sm font-semibold">Event coordination</p>
                  <p class="text-sm text-gray-300">Share posts, feedback, arrival updates, and event information with guests.</p>
                </div>
              </div>
            </div>
            <div class="rounded-3xl bg-white p-6 text-ink shadow-card ring-1 ring-gray-100">
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-semibold text-ink">Live events pipeline</p>
                  <p class="text-3xl font-semibold text-brand">One view</p>
                </div>
                <span class="rounded-full bg-brand/10 px-3 py-1 text-xs font-semibold text-brand">Shared across teams</span>
              </div>
              <div class="mt-5 space-y-3 text-sm text-muted">
                <div class="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
                  <div>
                    <p class="font-semibold text-ink">Campus Bash</p>
                    <p>Vendor shortlist • Guest responses</p>
                  </div>
                  <span class="text-brand">On track</span>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
                  <div>
                    <p class="font-semibold text-ink">Corporate Summit</p>
                    <p>Venue options • Attendee updates</p>
                  </div>
                  <span class="text-brand">Venue review</span>
                </div>
                <div class="flex items-center justify-between rounded-2xl bg-gray-50 px-4 py-3">
                  <div>
                    <p class="font-semibold text-ink">Charity 5K</p>
                    <p>Audio, stage, permits requested</p>
                  </div>
                  <span class="text-brand">Support</span>
                </div>
              </div>
              <div class="mt-6 rounded-2xl bg-gray-50 p-4">
                <p class="text-sm font-semibold text-ink">Spin up your next event</p>
                <p class="text-sm text-muted">Create an account to start an invitation, organize guests, and explore event services.</p>
                <RouterLink class="mt-3 inline-flex items-center gap-2 rounded-2xl bg-brand px-4 py-3 text-sm font-semibold text-white transition hover:bg-brandDark" to="/register">Create an event <i class="ri-arrow-right-line"></i></RouterLink>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import { useCases } from '@/data/useCases'

export default {
  name: 'UseCasesView',
  data () {
    return {
      useCases
    }
  }
}
</script>
